/* global React */
import { Box, CircularProgress } from '@mui/material'
import { getThemeColor } from 'lib/util'
import { useMemoRef, useStyles } from '@platform/react/hook'

const SIZE = '1.5rem'

const styles = (theme, { autoHide, duration, color, shouldHide, radius, background }) => ({
  root: {
    width: `calc(2 * ${radius})`,
    height: `calc(2 * ${radius})`,
    position: 'relative',
    borderRadius: '50%',
    backgroundColor: background
      ? getThemeColor(theme, background)
      : theme.palette.white.main,
    transition: `opacity ${duration / 2}ms linear`,
    ...autoHide && { opacity: shouldHide ? 0 : 1 },
  },
  ring: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: 'auto',
    '& .MuiCircularProgress-circle': {
      stroke: color ? getThemeColor(theme, color) : theme.palette.black.main,
    },
  },
  track: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: 'auto',
    '& .MuiCircularProgress-circle': {
      stroke: theme.palette.gray[700],
    },
  },
})

/**
 * Progress Circle Element
 *
 * @param {Object} p
 * @param {React.Ref} p.forwardedRef    ref for the element
 * @param {number} [p.duration=300]     ms for the fade transition
 * @param {string} [p.color]            color of the ring, defaults to black
 * @param {boolean} [p.hidden]          whether the component should be hidden
 * @param {boolean} [p.autoHide=true]   whether to only show the ring while loading
 * @param {boolean} [p.indeterminate]   show a spinning animation (over a track) instead of a value
 * @param {number} p.value              the current loading value
 * @returns {React.JSX.Element|null}
 */
const ProgressCircle = ({
  forwardedRef,
  value,
  radius = '1rem',

  hidden,
  autoHide = true,

  duration = 300,
  color = 'black.main',
  background = 'white.main',
  indeterminate = false,
  style = {},
  containerStyle = {},
}) => {
  const maxValue = Math.max(0, Math.min(100, value || 0))
  // Indeterminate rings are always visible; determinate ones fade out once there's nothing to show.
  const shouldHide = !indeterminate && autoHide && (maxValue === 0 || maxValue === 100)
  const classes = useStyles(styles, { shouldHide, autoHide, color, background, duration, radius })()

  return hidden ? null : (
    <Box
      ref={forwardedRef}
      style={containerStyle}
      className={classes.root}
    >
      {indeterminate && (
        <CircularProgress
          size={SIZE}
          value={100}
          variant={'determinate'}
          className={classes.track}
        />
      )}
      <CircularProgress
        size={SIZE}
        value={maxValue}
        style={style}
        variant={indeterminate ? 'indeterminate' : 'determinate'}
        className={classes.ring}
      />
    </Box>
  )
}

export default useMemoRef(ProgressCircle, props => [props.value, props.indeterminate])
